<template>
  <div
    class="absolute flex items-center gap-1 h-9 border border-n-700 bg-n-800 rounded w-36 text-xs pl-1 top-[1px] overflow-hidden"
    :style="{
      left: `${positionX}rem`,
      width: `${durationWidth}rem`
    }"
  >
    <l-icon name="floorplan" />
    <div class="truncate">
      <span v-if="tab.code">
        <span>{{ tab.code }}</span>
        <span v-if="tab.name"> - {{ tab.name }}</span>
      </span>
      <span v-else>{{ tab.name }}</span>
    </div>
    <div class="ml-auto flex items-center gap-1 shrink-0 h-full">
      <div class="shrink-0 w-5 bg-n-700 h-full flex items-center justify-center text-n-300">
        {{ tab.seats?.length }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { differenceInMinutes, isToday } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { LIcon } from '@last/core-ui/paprika'

import { useConfigStore } from '@/store/config'
import type { Tab } from '@/types'

import { LEFT_SHIFT, startOfWorkingHour } from './timeline.utils'

const props = defineProps<{
  tab: Tab
  slotWidth: number
}>()

const { config } = storeToRefs(useConfigStore())

const positionX = computed(() => {
  const time = props.tab.activationTime!
  if (!isToday(time)) {
    return 0
  }
  const minutes = differenceInMinutes(time, startOfWorkingHour(time))
  return (minutes / 30) * props.slotWidth + LEFT_SHIFT
})

const durationWidth = computed(() => {
  let durationMinutes = 60
  const reservationDurations = config.value.reservationDurations

  if (reservationDurations) {
    if (reservationDurations.type === 'ALL') {
      durationMinutes = reservationDurations.value
    } else if (reservationDurations.type === 'QUANTITY') {
      const seatCount = props.tab.seats?.length || 1
      const durationConfig =
        seatCount > 8
          ? reservationDurations.values.find(config => config.type === '8+')
          : reservationDurations.values.find(config => {
              return config.type.includes(seatCount.toString())
            })

      if (durationConfig) {
        durationMinutes = durationConfig.value
      }
    }
  }

  const activationTime = new Date(props.tab.activationTime!)
  if (!isToday(activationTime)) {
    const startOfToday = startOfWorkingHour(new Date())
    const remainingMinutes = differenceInMinutes(durationMinutes, startOfToday)
    durationMinutes = Math.min(durationMinutes, remainingMinutes)
  }

  return (durationMinutes / 30) * props.slotWidth
})
</script>
